import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    SavedCreditCard,
    SavedCreditCardWithCloMetaData,
} from 'common/api/e-comm/models/CreditCard';
import { createStyles } from 'utils/createStyle';
import {
    deleteCreditCardThunk,
    selectSavedCreditCardsWithChildIxoPayCards,
    setCardEnrolledInRewardsNetwork,
} from 'common/features/store/duck/account/duck';
import { useDispatch } from 'react-redux';
import { EditableFieldsContext } from '../context';
import { Btn } from 'components/btn/Btn';
import useMediaQueries from 'hooks/useMediaQueries';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { Radio, RadioOption } from 'components/radio/Radio';
import classnames from 'classnames';
import { useRootSelector } from 'common/features/featuresReducer';
import { FeatureFlagTypes, useFeatureFlags } from 'common/hooks/useFeatureFlags';
import { CheckboxField } from 'components/fields/TextField';
import Skeleton from 'react-loading-skeleton';
import { Modal } from 'components/modal/Modal';
import {
    enrollExistingIxoPayCardInRewardsNetworkThunk,
    unEnrollExistingIxoPayCardInRewardsNetworkThunk,
} from 'common/features/store/duck/local-dining/duck';
import { CardLink, WarningTriangle, CheckMark } from 'components/custom-icons';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { logCardEnrolledLocalDining } from 'config/analytics';

interface CreditCardFieldProps {
    isEditing: boolean;
    cardLinkedOffers?: boolean;
}

const styles = createStyles({
    container: ['flex flex-col'],
    enabledContainer: ['opacity-100'],
    disabledContainer: ['opacity-20'],
    styledIndicator: [
        'bg-black text-brand block',
        {
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: 'dodgerblue',
        },
    ],
    styledRadio: [
        'align-middle mr-2 cursor-pointer',
        {
            appearance: 'none',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            borderRadius: '50%',
            boxShadow: 'inset 0 0 0 1px gainsboro',
            minWidth: 15,
            width: 15,
            height: 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',

            '& ~ &': { marginLeft: 5 },

            '&:focus': {
                outline: 'none',
                boxShadow: 'inset 0 0 0 1px dodgerblue, 0 0 0 1px dodgerblue',
            },
        },
    ],
    text: [
        'text-grey-1',
        {
            fontStyle: 'normal',
            fontWeight: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
        },
    ],
    header: ['text-grey-2 text-sm mb-2.5'],
    delete: [
        'text-brand text-xs',
        { fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' },
    ],
    card: ['text-lg text-brand-dark mb-3', {}],
});

const createCloCreditCardDeleteWarningText = (card: SavedCreditCardWithCloMetaData) => {
    let lastFour;

    if (!card.maskedCardNumber) {
        lastFour = null;
    } else {
        const [_lastFour] = card.maskedCardNumber.match(/\d{4}$/) || [null];
        lastFour = _lastFour;
    }

    return `By deleting this card${
        lastFour ? ` ending in ${lastFour}` : ''
    }, you will no longer be able to use it for gift card purchases. To delete a card from local dining, navigate to the RaiseRight app: Local Dining > My Cards.`;
};

const createCreditCardDeleteWarningText = (card: SavedCreditCardWithCloMetaData) => {
    return `Are you sure you want to delete ${createCreditCardTitle(card)}?`;
};

const createCreditCardTitle = (card: SavedCreditCard) => {
    if (!card.maskedCardNumber) {
        return '';
    }

    return card.creditCardType + ' **' + card.maskedCardNumber.slice(2);
};

export const CreditCardField = ({ isEditing, cardLinkedOffers }: CreditCardFieldProps) => {
    const dispatch = useDispatch<any>();
    const { isEnabled } = useFeatureFlags();

    const [loadingCards, setLoadingCards] = useState<Record<string, boolean>>({});
    const [cardLinkingError, setCardLinkingError] = useState<string | null>(null);
    const [showTAndCModal, setShowTAndCModal] = useState(false);

    const { setFieldToEdit, fieldToEdit } = useContext(EditableFieldsContext);

    const savedCreditCards: SavedCreditCardWithCloMetaData[] = useRootSelector(
        selectSavedCreditCardsWithChildIxoPayCards
    );

    const [selectedCard, setSelectedCard] = useState<string | undefined>(undefined);
    const [selectedCardTitle, setSelectedCardTitle] = useState<string | undefined>('');
    const [deleteText, setDeleteText] = useState<string>('Delete a card');
    const [smallButtons, setSmallButtons] = useState<boolean>(false);
    const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const endEditMode = useCallback(() => {
        setFieldToEdit('');
        setSelectedCard(undefined);
        setDeleteSuccess(false);
    }, [setFieldToEdit, setSelectedCard, setDeleteSuccess]);

    const confirmDelete = useCallback(async () => {
        setIsLoading(true);
        const res = await deleteCreditCardThunk(parseInt(selectedCard as string))(dispatch);
        setIsLoading(false);

        if (res.error) {
            return;
        }
        setDeleteSuccess(true);
        setTimeout(() => {
            endEditMode();
        }, 3000);
    }, [selectedCard, dispatch, endEditMode]);

    const startEditMode = () => {
        setFieldToEdit('creditcardfield');
    };

    const onValueChange = (option: RadioOption) => {
        const { value } = option;
        const card = savedCreditCards?.find((card) => card.id.toString() === value);
        if (card) {
            setSelectedCardTitle(
                cardLinkedOffers && card?.ixoPayCardId
                    ? createCloCreditCardDeleteWarningText(card)
                    : createCreditCardDeleteWarningText(card)
            );
            setSelectedCard(card.id.toString());
        }
    };

    const queries = useMemo(
        () => ({
            '(min-width: 769px)': () => {
                setDeleteText('Delete a card');
                setSmallButtons(false);
            },
            '(max-width: 768px)': () => {
                setDeleteText('Delete');
                setSmallButtons(true);
            },
        }),
        []
    );

    function removeLoadingCard(cardId: string | number) {
        const nextLoadingCards = { ...loadingCards };
        delete nextLoadingCards[cardId];
        setLoadingCards(nextLoadingCards);
    }

    const [onCloTermsAndConditionsAccepted, setOnClowTermsAndConditionsAccepted] = useState<
        () => void
    >(() => null);
    const enrollCardInCLO = async (willEnroll: boolean, card: SavedCreditCardWithCloMetaData) => {
        const childId = card.ixoPayCardId || null;
        if (childId === null) {
            return;
        }
        setCardLinkingError(null);

        if (willEnroll) {
            setOnClowTermsAndConditionsAccepted(() => async () => {
                setLoadingCards({ ...loadingCards, [card.id]: true });
                try {
                    await dispatch(enrollExistingIxoPayCardInRewardsNetworkThunk(childId));
                    dispatch(setCardEnrolledInRewardsNetwork({ id: card.id, status: true }));
                    removeLoadingCard(card.id);
                    logCardEnrolledLocalDining(true, 'payment_options');
                } catch (error) {
                    setCardLinkingError(error as string);
                    logCardEnrolledLocalDining(false, 'payment_options');
                }
            });
            setShowTAndCModal(true);
        } else {
            setOnClowTermsAndConditionsAccepted(() => null);
            setLoadingCards({ ...loadingCards, [card.id]: true });
            try {
                await dispatch(unEnrollExistingIxoPayCardInRewardsNetworkThunk(childId));
                dispatch(setCardEnrolledInRewardsNetwork({ id: card.id, status: false }));
            } catch (error) {
                setCardLinkingError(error as string);
            } finally {
                requestAnimationFrame(() => removeLoadingCard(card.id));
            }
        }
    };

    useMediaQueries(queries);

    if (!savedCreditCards || savedCreditCards.length < 1) {
        return (
            <div>
                <div className={styles.header}>
                    {isEnabled(FeatureFlagTypes.enable_debit_web)
                        ? 'Debit and Credit Cards'
                        : 'Credit Card'}
                </div>
                <div className={styles.text}>
                    {isEnabled(FeatureFlagTypes.enable_debit_web) ? 'Debit and c' : 'C'}redit cards
                    can only be added during checkout.
                </div>
            </div>
        );
    }

    return (
        <div
            className={`payment_options_credit_card_section ${classnames(
                styles.container,
                fieldToEdit && fieldToEdit !== 'creditcardfield'
                    ? styles.disabledContainer
                    : styles.enabledContainer
            )}`}
        >
            <div className="flex flex-row justify-between mb-2">
                <div className={styles.header}>
                    {isEnabled(FeatureFlagTypes.enable_debit_web)
                        ? 'Credit and Debit Cards'
                        : 'Credit Card'}
                </div>

                {isEditing ? (
                    <button onClick={endEditMode} className={styles.delete}>
                        Cancel
                    </button>
                ) : (
                    <button onClick={startEditMode} className={styles.delete}>
                        {deleteText}
                    </button>
                )}
            </div>

            <div>
                {isEditing ? (
                    <div data-recording-sensitive>
                        <Radio
                            options={savedCreditCards.map((card) => {
                                return {
                                    value: card.id.toString(),
                                    text: (
                                        <CardOption
                                            card={card}
                                            showCardLinkedOffers={Boolean(cardLinkedOffers)}
                                            loadingCards={loadingCards}
                                            enrollCardHandler={() => null}
                                            disabled
                                        />
                                    ),
                                };
                            })}
                            extraStyles="mb-0"
                            value={selectedCard || ''}
                            onChange={onValueChange}
                        ></Radio>
                    </div>
                ) : (
                    <>
                        {savedCreditCards.map((card) => {
                            return (
                                <CardOption
                                    card={card}
                                    showCardLinkedOffers={Boolean(cardLinkedOffers)}
                                    loadingCards={loadingCards}
                                    enrollCardHandler={enrollCardInCLO}
                                />
                            );
                        })}

                        {cardLinkingError && <CardLinkingErrorMessage />}

                        {cardLinkedOffers && onCloTermsAndConditionsAccepted && (
                            <CardLinkingTermsModal
                                onTermsAccepted={onCloTermsAndConditionsAccepted}
                                showModal={showTAndCModal}
                                dismissModal={() => setShowTAndCModal(false)}
                            />
                        )}
                    </>
                )}
            </div>

            {!deleteSuccess ? (
                <div
                    className="border-t border-r-0 border-l-0 border-b-0 border-grey-5"
                    hidden={!isEditing}
                >
                    {selectedCard && (
                        <div>
                            {!isLoading ? (
                                <>
                                    <div className="mt-5 mb-5 text-error">
                                        <span data-recording-sensitive>{selectedCardTitle}</span>
                                    </div>

                                    <Btn
                                        className="mr-3"
                                        isSmall={smallButtons}
                                        isSecondary
                                        onClick={endEditMode}
                                    >
                                        No, Cancel
                                    </Btn>
                                    <Btn
                                        className="ml-3"
                                        isSmall={smallButtons}
                                        onClick={confirmDelete}
                                    >
                                        Yes, Delete
                                    </Btn>
                                </>
                            ) : (
                                <div className="w-full flex justify-center">
                                    <ActivityIndicator />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex border-t border-r-0 border-l-0 border-b-0 border-grey-5 pt-2">
                    <CheckMark />
                    <div className="ml-2">
                        Successfully deleted
                        <span data-recording-sensitive>{selectedCardTitle}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export const CardLinkingStatusAndButton = ({
    isLoading,
    card,
    enrollCardHandler,
    disabled,
}: {
    isLoading: boolean;
    card: any;
    enrollCardHandler: Function;
    disabled?: boolean;
}) => {
    return (
        <div>
            {isLoading ? (
                <Skeleton height={28} />
            ) : card.enrolledInRewardsNetwork ? (
                <CheckboxField
                    label={
                        <>
                            <span className="text-orange text-sm pr-1">New!</span>{' '}
                            <span className="text-sm text-grey-darkest">
                                Link to earn with local dining
                            </span>
                        </>
                    }
                    onStateChange={() => enrollCardHandler(false, card)}
                    checked={true}
                    disabled={disabled}
                />
            ) : (
                <button
                    onClick={() => enrollCardHandler(true, card)}
                    className="text-sm bg-pale-blue px-4 py-2 rounded-lg mt-1"
                >
                    <span className="text-orange text-sm font-semibold">New!</span>{' '}
                    <span className="underline text-sm text-brand font-semibold">
                        Link to earn with local dining
                    </span>
                </button>
            )}
        </div>
    );
};

export const CardOption = ({
    card,
    showCardLinkedOffers,
    loadingCards,
    enrollCardHandler,
    disabled,
}: {
    card: any;
    showCardLinkedOffers: boolean;
    loadingCards: Record<string, boolean>;
    enrollCardHandler: Function;
    disabled?: boolean;
}) => {
    return (
        <div data-recording-sensitive key={card.id} className={styles.card}>
            <div className="">
                <div>
                    <span className="text-lg text-brand-dark font-semibold">
                        {card.cardSubType === 'Debit' ? 'Debit' : 'Credit'} Card{' '}
                    </span>

                    <span className="text-lg text-grey-1">({createCreditCardTitle(card)})</span>
                </div>

                {showCardLinkedOffers && card.ixoPayCardId && (
                    <CardLinkingStatusAndButton
                        isLoading={loadingCards[card.id]}
                        card={card}
                        enrollCardHandler={enrollCardHandler}
                        disabled={disabled}
                    />
                )}
            </div>
        </div>
    );
};

export const CardLinkingErrorMessage = () => {
    return (
        <div className="flex items-center border border-blush-red bg-lightest-pink px-5 py-3 !text-sm">
            <WarningTriangle height="22" width="22" className="inline-block" />
            <span className="text-error pl-3 self-baseline">
                Something went wrong linking your card. Please try again.
            </span>
        </div>
    );
};

export interface CardLinkingTermsModalProps {
    showModal: boolean;
    onTermsAccepted: () => void;
    dismissModal: () => void;
}

const modalStyles = createStyles({
    checkboxErrorWrapper: {
        '& button[role="checkbox"]': {
            borderColor: 'red !important',
        },
    },
    buttonNoBorder: {
        '& button': {
            border: 'none',
            textDecoration: 'underline',
        },
    },
    headerText: [
        'pb-2 text-brand-dark font-semibold text-24px',
        {
            paddingTop: '8px',
            lineHeight: '32px',
        },
    ],
    subHeaderText: [
        'text-grey-darkest pb-2 text-16px',
        {
            lineHeight: '24px',
            paddingBottom: '40px',
        },
    ],
    bodyText: [
        'pt-2 content-start text-left text-grey-darkest font-light text-14px',
        {
            lineHeight: '20px',
        },
    ],
});

export const CardLinkingTermsModal = ({
    showModal,
    onTermsAccepted,
    dismissModal,
}: CardLinkingTermsModalProps) => {
    const [showValidationWarnings, setShowValidationWarnings] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const onClose = () => {
        setTermsAccepted(false);
        setShowValidationWarnings(false);
        dismissModal();
    };

    const _onTermsAccepted = () => {
        if (!termsAccepted) {
            setShowValidationWarnings(true);
            return;
        }

        onClose();
        onTermsAccepted();
    };

    const renderWarnings = showValidationWarnings && !termsAccepted;
    const earnings = useFirebaseConfigValues('local_dining_earnings', '5', (v: any) =>
        v.asString()
    );

    return (
        <Modal isOpen={showModal} title={undefined} onClose={onClose}>
            <div className="flex flex-col align-center center text-center">
                <div className="flex flex-row items-center justify-center">
                    <CardLink />
                </div>

                <p className="pt-4 italic text-brand-dark font-regular text-16px">
                    You're almost there!
                </p>

                <h2 className={modalStyles.headerText}>
                    Earn {earnings}% at participating local restaurants when you pay with a linked
                    card
                </h2>

                <h4 className={modalStyles.subHeaderText}>
                    To find restaurants open the RaiseRight app and navigate to Local Dining from
                    the home screen.
                </h4>

                <span className={renderWarnings ? modalStyles.checkboxErrorWrapper : ''}>
                    <CheckboxField
                        label={
                            <span className="text-grey-darkest font-semibold text-14px pl-1">
                                Accept Terms &amp; Conditions
                            </span>
                        }
                        onStateChange={(next) => setTermsAccepted(next)}
                        checked={termsAccepted}
                    />
                </span>

                <p className={modalStyles.bodyText}>
                    I authorize payment networks (e.g. American Express, Mastercard, and Visa) to
                    monitor activity on my linked payment card(s) to identify qualifying
                    transactions, and for such payment networks to share transaction details with
                    Rewards Network to enable my card-linked offer, present me with targeted offers
                    that may be of interest to me, and otherwise facilitate my participation in
                    RaiseRight’s local dining program as set forth in the Terms of Use and Privacy
                    Policy. I understand I may opt out of transaction monitoring on my linked
                    payment card(s) by removing my card at any time by following the instructions
                    provided within the Help Center.
                </p>

                {renderWarnings && (
                    <div className="text-error content-start text-left font-normal text-14px">
                        Please select the ‘Accept Terms & Conditions’ checkbox above to continue.
                    </div>
                )}

                <div className="pb-4 pt-5">
                    <Btn onClick={_onTermsAccepted}>Link Card</Btn>
                </div>

                <div className={modalStyles.buttonNoBorder}>
                    <Btn isSecondary onClick={onClose}>
                        Cancel
                    </Btn>
                </div>
            </div>
        </Modal>
    );
};
